/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { TrackedArray } from 'tracked-built-ins';
import { get, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import titleize from 'ember-cli-string-helpers/utils/titleize';
import { classNameBindings, classNames } from '@ember-decorators/component';
import { byKeys, compact } from '@linc-technologies/linced-common/utils/arrays';

@classNames('layout-column', 'import-field-mapper')
@classNameBindings('hideAutoMapped:hide-auto-mapped')
export default class ImportFieldMapper extends Component {
  @tracked
  singular = 'field';

  @tracked
  plural = 'fields';

  get singularTitle() {
    return titleize(this.singular);
  }

  get pluralTitle() {
    return titleize(this.plural);
  }

  showMeta = true;

  @tracked
  hideAutoMapped = false;

  hideColumns = [];

  // Plain getter (not @computed): TrackedArray's reactivity is autotracking-
  // based, not classic-dependent-key-based, so this needs to read `column`
  // during its own execution to pick up per-slot mutations from dropField.
  get allColumnsHidden() {
    let numHidden = this.destinations.reduce((num, column, index) => {
      if (this.hideAutoMapped && column.autoMapped) {
        num++;
      } else if (this.hideColumns.includes(index)) {
        num++;
      }
      return num;
    }, 0);

    return numHidden === this.destinations.length;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    let destinations = [];
    if (!isEmpty(this.default)) {
      destinations = this.default;
    } else if (this.columns) {
      this.columns.forEach((col, index) => {
        // TrackedArray, not a plain array: dropField mutates each column's
        // slot in place (push/splice) via DragSortList's @items, and needs
        // that mutation to be independently reactive without reassigning
        // the outer `destinations` array on every drag.
        let destination = new TrackedArray();
        destination.columnIndex = index;
        destinations.push(destination);
      });
    }
    this.destinations = destinations;
    this.currentTab = this.fieldTabs[0];

    // Trigger an onChange so that the caller receives the default map (whether provided or empty).
    next(this, function () {
      this.onChange(this.flatDestinations);
    });
  }

  get flatDestinations() {
    return this.destinations.map((slot) => slot[0]);
  }

  get flatDestinationsById() {
    return this.destinations.map((slot) => {
      let destination = slot[0];
      if (isEmpty(destination)) {
        return null;
      }
      return {
        key: destination.key,
        fieldId: destination.field.id,
      };
    });
  }

  // Plain getter, not @computed: this reads flatDestinationsById, itself a
  // plain getter over TrackedArray-backed destinations. A classic @computed
  // dependent-key string can't reliably observe a plain native getter's
  // autotracked reads, so keeping every getter downstream of `destinations`
  // as a plain getter (not mixing in classic @computed) is what makes the
  // whole chain react correctly to dropField's mutations.
  get unmappedFields() {
    let destinations = compact(this.flatDestinationsById);
    let fieldMap = {};
    this.fieldTabs.forEach((key) => {
      fieldMap[key] = this.fields[key]
        .filter((field) => {
          if (field.field.dataType === DataType.LIST) {
            // Always include "list" fields as unmapped/available, so they can be mapped to multiple fields.
            return true;
          }
          return !destinations.find((destination) => {
            return (
              destination.key === key && destination.fieldId === field.field.id
            );
          });
        })
        .sort(byKeys('field.group.sequence', 'field.sequence'));
    });
    return fieldMap;
  }

  @computed('fields')
  get fieldTabs() {
    return Object.keys(this.fields);
  }

  @tracked
  filterValue = '';

  @action
  setFilterValue(value) {
    this.filterValue = value;
  }

  @tracked
  currentTab;

  @action
  setCurrentTab(value) {
    this.currentTab = value;
  }

  @tracked
  destinations;

  get filteredFields() {
    let tabFields = this.unmappedFields[this.currentTab];
    if (!isEmpty(this.filterValue)) {
      tabFields = tabFields.filter((field) => {
        return this.matchField(field.field, this.filterValue) > -1;
      });
    }
    return tabFields.sort(
      byKeys(
        'field.group.sequence',
        'field.group.name',
        'field.sequence',
        'field.label',
      ),
    );
  }

  matchField(field, term) {
    let text = stripDiacritics(term).toLowerCase();
    let labelMatch = stripDiacritics(field.label).toLowerCase().indexOf(text);
    let keyMatch = stripDiacritics(field.key).toLowerCase().indexOf(text);
    let groupMatch = stripDiacritics(get(field, 'group.name'))
      .toLowerCase()
      .indexOf(text);
    return Math.max(labelMatch, keyMatch, groupMatch);
  }

  @action
  dropField({ draggedItem, sourceList, targetList }) {
    let targetIndex = targetList.columnIndex ?? -1;

    if (targetIndex > -1) {
      // Dropped into a column map - a column slot only ever holds one field.
      targetList.splice(0, targetList.length);
      if (draggedItem.field.dataType === DataType.LIST) {
        // For List fields, clone them so that they can be given different index properties later.
        let clone = {
          key: draggedItem.key,
          field: draggedItem.field,
        };
        targetList.push(clone);
      } else {
        targetList.push(draggedItem);
      }
    }
    let sourcePos = sourceList.indexOf(draggedItem);
    if (sourcePos > -1) {
      // Removed from a column map (dragged elsewhere). A no-op if
      // draggedItem came from the unmapped-fields pool rather than a column,
      // matching removeObject's own no-op-if-absent behaviour.
      sourceList.splice(sourcePos, 1);
    }

    this.onChange(this.flatDestinations);
  }
}
